/* ------------------------------------------------------------------ */
/*  Preset challenge scenarios                                        */
/* ------------------------------------------------------------------ */

import type { Scenario } from './types';

export const scenarios: Scenario[] = [
  /* ---------------------------------------------------------------- */
  /*  1. SSH Brute Force (beginner)                                   */
  /* ---------------------------------------------------------------- */
  {
    id: 'ssh-brute-force',
    title: 'SSH Brute Force',
    difficulty: 'beginner',
    description:
      'Detect SSH brute-force attempts from auth.log. You need a decoder to extract the source IP and a rule that triggers on repeated failed authentication.',
    objective:
      'Write a decoder and rule that flag brute-force SSH login attempts while ignoring legitimate authentications.',
    logs: [
      {
        log: 'Jun 14 15:16:01 web01 sshd[2001]: Failed password for invalid user admin from 10.0.0.50 port 22 ssh2',
        malicious: true,
        reason: 'Failed login for invalid user from attacker IP',
      },
      {
        log: 'Jun 14 15:16:02 web01 sshd[2002]: Failed password for invalid user root from 10.0.0.50 port 22 ssh2',
        malicious: true,
        reason: 'Failed login for invalid user from same attacker IP',
      },
      {
        log: 'Jun 14 15:16:03 web01 sshd[2003]: Failed password for invalid user test from 10.0.0.50 port 22 ssh2',
        malicious: true,
        reason: 'Failed login for invalid user from same attacker IP',
      },
      {
        log: 'Jun 14 15:16:04 web01 sshd[2004]: Failed password for invalid user guest from 10.0.0.50 port 22 ssh2',
        malicious: true,
        reason: 'Failed login for invalid user from same attacker IP',
      },
      {
        log: 'Jun 14 15:17:01 web01 sshd[2010]: Accepted publickey for deploy from 192.168.1.10 port 22 ssh2',
        malicious: false,
        reason: 'Legitimate key-based authentication',
      },
      {
        log: 'Jun 14 15:18:30 web01 sshd[2015]: Accepted password for admin from 192.168.1.20 port 22 ssh2',
        malicious: false,
        reason: 'Legitimate password authentication',
      },
      {
        log: 'Jun 14 15:20:00 web01 CRON[3001]: pam_unix(cron:session): session opened for user root',
        malicious: false,
        reason: 'Normal cron session',
      },
      {
        log: 'Jun 14 15:21:00 web01 systemd-logind[500]: New session 42 of user admin.',
        malicious: false,
        reason: 'Normal systemd session',
      },
      {
        log: 'Jun 14 15:22:00 web01 sshd[2020]: Connection closed by 192.168.1.10 port 22',
        malicious: false,
        reason: 'Normal connection close',
      },
      {
        log: 'Jun 14 15:23:00 web01 sshd[2025]: Server listening on 0.0.0.0 port 22.',
        malicious: false,
        reason: 'Normal sshd startup message',
      },
    ],
    decoderHint: `<!-- Hint: Match the sshd program and extract fields from failed login lines -->
<decoder name="sshd-failed">
  <program_name>sshd</program_name>
  <prematch>Failed password</prematch>
  <!-- Add regex and order to extract the source IP -->
</decoder>`,
    ruleHint: `<!-- Hint: Match on the decoder and the "Failed password" pattern -->
<rule id="100001" level="5">
  <!-- Add decoded_as, match, and description -->
</rule>`,
    solutionDecoder: `<decoder name="sshd-failed">
  <program_name>sshd</program_name>
  <prematch>Failed password</prematch>
  <regex>Failed password for \\S+ user \\S+ from (\\S+) port</regex>
  <order>srcip</order>
</decoder>`,
    solutionRule: `<rule id="100001" level="5">
  <decoded_as>sshd-failed</decoded_as>
  <description>SSH failed login attempt</description>
</rule>

<rule id="100002" level="10">
  <if_sid>100001</if_sid>
  <frequency>4</frequency>
  <timeframe>120</timeframe>
  <same_source_ip />
  <description>SSH brute force detected</description>
  <mitre>
    <id>T1110</id>
    <tactic>Credential Access</tactic>
  </mitre>
</rule>`,
  },

  /* ---------------------------------------------------------------- */
  /*  2. Web Shell Detection (intermediate)                           */
  /* ---------------------------------------------------------------- */
  {
    id: 'web-shell',
    title: 'Web Shell Detection',
    difficulty: 'intermediate',
    description:
      'Detect web shell activity in Apache access logs. Suspicious patterns include requests to unusual PHP files with command-execution parameters.',
    objective:
      'Write rules that detect web shell access patterns while allowing normal web traffic through.',
    logs: [
      {
        log: 'Jun 14 10:00:01 web01 apache[5001]: 10.0.0.99 - - "GET /uploads/shell.php?cmd=whoami HTTP/1.1" 200 45',
        malicious: true,
        reason: 'Web shell with cmd parameter',
      },
      {
        log: 'Jun 14 10:00:05 web01 apache[5002]: 10.0.0.99 - - "GET /uploads/shell.php?cmd=cat+/etc/passwd HTTP/1.1" 200 1024',
        malicious: true,
        reason: 'Web shell reading sensitive files',
      },
      {
        log: 'Jun 14 10:00:10 web01 apache[5003]: 10.0.0.99 - - "POST /images/thumb.php HTTP/1.1" 200 0',
        malicious: true,
        reason: 'POST to suspicious PHP file in images directory',
      },
      {
        log: 'Jun 14 10:01:00 web01 apache[5010]: 192.168.1.5 - - "GET /index.html HTTP/1.1" 200 5120',
        malicious: false,
        reason: 'Normal page request',
      },
      {
        log: 'Jun 14 10:01:01 web01 apache[5011]: 192.168.1.5 - - "GET /css/style.css HTTP/1.1" 200 2048',
        malicious: false,
        reason: 'Normal static asset',
      },
      {
        log: 'Jun 14 10:01:02 web01 apache[5012]: 192.168.1.5 - - "GET /js/app.js HTTP/1.1" 200 4096',
        malicious: false,
        reason: 'Normal static asset',
      },
      {
        log: 'Jun 14 10:02:00 web01 apache[5020]: 192.168.1.10 - - "POST /api/login HTTP/1.1" 200 256',
        malicious: false,
        reason: 'Normal API call',
      },
      {
        log: 'Jun 14 10:02:30 web01 apache[5021]: 192.168.1.10 - - "GET /api/users HTTP/1.1" 200 512',
        malicious: false,
        reason: 'Normal API call',
      },
      {
        log: 'Jun 14 10:03:00 web01 apache[5030]: 192.168.1.15 - - "GET /admin/dashboard.php HTTP/1.1" 200 8192',
        malicious: false,
        reason: 'Normal admin page',
      },
      {
        log: 'Jun 14 10:03:30 web01 apache[5031]: 192.168.1.15 - - "GET /favicon.ico HTTP/1.1" 404 0',
        malicious: false,
        reason: 'Normal 404 for missing favicon',
      },
    ],
    decoderHint: `<!-- Hint: Match apache logs and extract the source IP and request URI -->
<decoder name="apache-access">
  <program_name>apache</program_name>
  <!-- Add regex to capture srcip and the request path -->
</decoder>`,
    ruleHint: `<!-- Hint: Look for .php files in upload/image directories with cmd parameters -->
<rule id="100010" level="12">
  <!-- What regex pattern would match web shell requests? -->
</rule>`,
    solutionDecoder: `<decoder name="apache-access">
  <program_name>apache</program_name>
  <regex>(\\S+) - - "(\\S+ \\S+)</regex>
  <order>srcip,url</order>
</decoder>`,
    solutionRule: `<rule id="100010" level="12">
  <decoded_as>apache-access</decoded_as>
  <regex>/(uploads|images)/\\S+\\.php</regex>
  <description>Web shell access detected in upload directory</description>
  <mitre>
    <id>T1505.003</id>
    <tactic>Persistence</tactic>
  </mitre>
</rule>`,
  },

  /* ---------------------------------------------------------------- */
  /*  3. Privilege Escalation via Sudo (intermediate)                 */
  /* ---------------------------------------------------------------- */
  {
    id: 'priv-escalation',
    title: 'Privilege Escalation via Sudo',
    difficulty: 'intermediate',
    description:
      'Detect suspicious sudo usage that could indicate privilege escalation. Focus on unauthorized users running sensitive commands.',
    objective:
      'Flag unauthorized sudo commands (shell spawning, password changes, shadow file access) while allowing routine admin tasks.',
    logs: [
      {
        log: 'Jun 14 12:00:01 db01 sudo[7001]: webuser : TTY=pts/0 ; PWD=/tmp ; USER=root ; COMMAND=/bin/bash',
        malicious: true,
        reason: 'Non-admin user spawning root shell',
      },
      {
        log: 'Jun 14 12:00:30 db01 sudo[7002]: webuser : TTY=pts/0 ; PWD=/tmp ; USER=root ; COMMAND=/usr/bin/passwd root',
        malicious: true,
        reason: 'Non-admin user changing root password',
      },
      {
        log: 'Jun 14 12:01:00 db01 sudo[7003]: webuser : TTY=pts/0 ; PWD=/tmp ; USER=root ; COMMAND=/bin/cat /etc/shadow',
        malicious: true,
        reason: 'Non-admin user reading shadow file',
      },
      {
        log: 'Jun 14 12:05:00 db01 sudo[7010]: admin : TTY=pts/1 ; PWD=/home/admin ; USER=root ; COMMAND=/usr/bin/systemctl restart nginx',
        malicious: false,
        reason: 'Admin restarting a service — routine',
      },
      {
        log: 'Jun 14 12:06:00 db01 sudo[7011]: admin : TTY=pts/1 ; PWD=/home/admin ; USER=root ; COMMAND=/usr/bin/apt update',
        malicious: false,
        reason: 'Admin running package updates — routine',
      },
      {
        log: 'Jun 14 12:07:00 db01 sudo[7012]: admin : TTY=pts/1 ; PWD=/home/admin ; USER=root ; COMMAND=/usr/bin/tail -f /var/log/syslog',
        malicious: false,
        reason: 'Admin reading logs — routine',
      },
      {
        log: 'Jun 14 12:08:00 db01 CRON[8001]: pam_unix(cron:session): session opened for user root',
        malicious: false,
        reason: 'Normal cron session',
      },
      {
        log: 'Jun 14 12:09:00 db01 CRON[8002]: (root) CMD (/usr/lib/sa/sa1 1 1)',
        malicious: false,
        reason: 'Normal cron job',
      },
      {
        log: 'Jun 14 12:10:00 db01 systemd[1]: Started Session 101 of user admin.',
        malicious: false,
        reason: 'Normal systemd session',
      },
      {
        log: 'Jun 14 12:11:00 db01 sshd[9001]: Accepted publickey for admin from 192.168.1.5 port 22 ssh2',
        malicious: false,
        reason: 'Normal SSH login',
      },
    ],
    decoderHint: `<!-- Hint: Match sudo logs and extract the username and command -->
<decoder name="sudo-command">
  <program_name>sudo</program_name>
  <!-- Extract the user running sudo and the COMMAND -->
</decoder>`,
    ruleHint: `<!-- Hint: Focus on non-admin users running dangerous commands -->
<rule id="100020" level="10">
  <!-- What patterns indicate suspicious sudo usage? -->
</rule>`,
    solutionDecoder: `<decoder name="sudo-command">
  <program_name>sudo</program_name>
  <regex>(\\S+) : TTY=\\S+ ; PWD=\\S+ ; USER=(\\S+) ; COMMAND=(.*)</regex>
  <order>srcuser,dstuser,command</order>
</decoder>`,
    solutionRule: `<rule id="100020" level="10">
  <decoded_as>sudo-command</decoded_as>
  <regex>webuser .* COMMAND=(/bin/bash|/bin/sh|/usr/bin/passwd|/bin/cat /etc/shadow)</regex>
  <description>Suspicious sudo command by non-admin user</description>
  <mitre>
    <id>T1548.003</id>
    <tactic>Privilege Escalation</tactic>
  </mitre>
</rule>`,
  },

  /* ---------------------------------------------------------------- */
  /*  4. Lateral Movement (advanced)                                  */
  /* ---------------------------------------------------------------- */
  {
    id: 'lateral-movement',
    title: 'Lateral Movement',
    difficulty: 'advanced',
    description:
      'Detect lateral movement patterns by combining suspicious service-account activity and chained high-confidence recon rules.',
    objective:
      'Use if_sid chaining to escalate suspicious sudo activity into a high-confidence lateral movement alert, while still flagging the initial suspicious login.',
    logs: [
      {
        log: 'Jun 14 09:00:01 app01 sshd[3001]: Accepted password for svcaccount from 10.0.0.200 port 22 ssh2',
        malicious: true,
        reason: 'Login from unusual internal IP with service account',
      },
      {
        log: 'Jun 14 09:00:05 app01 sudo[3002]: svcaccount : TTY=pts/0 ; PWD=/tmp ; USER=root ; COMMAND=/usr/bin/find / -name id_rsa',
        malicious: true,
        reason: 'Recon: searching for SSH keys after lateral move',
      },
      {
        log: 'Jun 14 09:00:10 app01 sudo[3003]: svcaccount : TTY=pts/0 ; PWD=/tmp ; USER=root ; COMMAND=/bin/cat /etc/passwd',
        malicious: true,
        reason: 'Recon: enumerating users after lateral move',
      },
      {
        log: 'Jun 14 09:05:00 app01 sshd[3010]: Accepted publickey for deploy from 192.168.1.100 port 22 ssh2',
        malicious: false,
        reason: 'Normal deployment login from known CI server',
      },
      {
        log: 'Jun 14 09:06:00 app01 sudo[3011]: deploy : TTY=pts/1 ; PWD=/opt/app ; USER=root ; COMMAND=/usr/bin/systemctl restart app',
        malicious: false,
        reason: 'Normal deployment restart',
      },
      {
        log: 'Jun 14 09:10:00 app01 sshd[3020]: Accepted publickey for admin from 192.168.1.5 port 22 ssh2',
        malicious: false,
        reason: 'Normal admin login',
      },
      {
        log: 'Jun 14 09:15:00 app01 CRON[4001]: pam_unix(cron:session): session opened for user root',
        malicious: false,
        reason: 'Normal cron session',
      },
      {
        log: 'Jun 14 09:16:00 app01 CRON[4002]: (root) CMD (/usr/local/bin/health-check.sh)',
        malicious: false,
        reason: 'Normal health check cron',
      },
      {
        log: 'Jun 14 09:20:00 app01 systemd[1]: Started Session 55 of user deploy.',
        malicious: false,
        reason: 'Normal session',
      },
      {
        log: 'Jun 14 09:25:00 app01 sshd[3030]: Connection closed by 192.168.1.100 port 22',
        malicious: false,
        reason: 'Normal connection close',
      },
    ],
    decoderHint: `<!-- Hint: You need TWO decoders — one for sshd logins, one for sudo commands -->
<decoder name="sshd-login">
  <program_name>sshd</program_name>
  <prematch>Accepted</prematch>
  <!-- Extract user and srcip -->
</decoder>

<decoder name="sudo-cmd">
  <program_name>sudo</program_name>
  <!-- Extract user and command -->
</decoder>`,
    ruleHint: `<!-- Hint: Use one base rule for suspicious login, then chain sudo recon -->
<rule id="100029" level="5">
  <!-- First: detect suspicious svcaccount SSH login -->
</rule>

<rule id="100030" level="4">
  <!-- Then: detect sudo commands by svcaccount -->
</rule>

<rule id="100031" level="12">
  <if_sid>100030</if_sid>
  <!-- Finally: high-confidence recon command pattern -->
</rule>`,
    solutionDecoder: `<decoder name="sshd-login">
  <program_name>sshd</program_name>
  <prematch>Accepted</prematch>
  <regex>Accepted \\S+ for (\\S+) from (\\S+) port</regex>
  <order>dstuser,srcip</order>
</decoder>

<decoder name="sudo-cmd">
  <program_name>sudo</program_name>
  <regex>(\\S+) : TTY=\\S+ ; PWD=\\S+ ; USER=(\\S+) ; COMMAND=(.*)</regex>
  <order>srcuser,dstuser,command</order>
</decoder>`,
    solutionRule: `<rule id="100029" level="5">
  <decoded_as>sshd-login</decoded_as>
  <match>svcaccount</match>
  <regex>from 10\\.0\\.0\\.200 port</regex>
  <description>Suspicious service account login</description>
</rule>

<rule id="100030" level="4">
  <decoded_as>sudo-cmd</decoded_as>
  <match>svcaccount</match>
  <description>Service account ran sudo command</description>
</rule>

<rule id="100031" level="12">
  <decoded_as>sudo-cmd</decoded_as>
  <match>svcaccount</match>
  <if_sid>100030</if_sid>
  <regex>(find.*id_rsa|cat /etc/passwd|cat /etc/shadow)</regex>
  <description>Lateral movement — recon after service account login</description>
  <mitre>
    <id>T1021</id>
    <tactic>Lateral Movement</tactic>
  </mitre>
</rule>`,
  },

  /* ---------------------------------------------------------------- */
  /*  5. Log Injection Evasion (advanced)                             */
  /* ---------------------------------------------------------------- */
  {
    id: 'log-injection',
    title: 'Log Injection Evasion',
    difficulty: 'advanced',
    description:
      'Attackers inject fake log entries to hide among legitimate traffic or cause false positives. Detect the real attacks and avoid being fooled by injected entries.',
    objective:
      'Distinguish genuine attack logs from injected decoy entries. Injected lines have subtle tells: wrong timestamp formats, impossible PIDs, or mismatched hostnames.',
    logs: [
      {
        log: 'Jun 14 14:00:01 fw01 sshd[6001]: Failed password for root from 203.0.113.50 port 22 ssh2',
        malicious: true,
        reason: 'Real brute force attempt from external IP',
      },
      {
        log: 'Jun 14 14:00:02 fw01 sshd[6002]: Failed password for admin from 203.0.113.50 port 22 ssh2',
        malicious: true,
        reason: 'Real brute force attempt from same external IP',
      },
      {
        log: 'Jun 14 14:00:03 fw01 sshd[6003]: Failed password for user from 203.0.113.50 port 22 ssh2',
        malicious: true,
        reason: 'Real brute force attempt from same external IP',
      },
      {
        log: 'Jun 14 02:00:01 fw01 sshd[99999]: Failed password for root from 192.168.1.1 port 22 ssh2',
        malicious: false,
        reason: 'Injected log: impossible PID (99999) and internal IP posing as attack',
      },
      {
        log: 'Jun 14 02:00:02 websvr sshd[99998]: Failed password for admin from 192.168.1.1 port 22 ssh2',
        malicious: false,
        reason: 'Injected log: hostname mismatch (websvr not fw01)',
      },
      {
        log: 'Jun 14 14:05:00 fw01 sshd[6010]: Accepted publickey for admin from 10.0.0.1 port 22 ssh2',
        malicious: false,
        reason: 'Legitimate admin login',
      },
      {
        log: 'Jun 14 14:06:00 fw01 CRON[7001]: pam_unix(cron:session): session opened for user root',
        malicious: false,
        reason: 'Normal cron session',
      },
      {
        log: 'Jun 14 14:07:00 fw01 kernel: [42000.123456] TCP: request_sock_TCP: Possible SYN flooding on port 80.',
        malicious: false,
        reason: 'Kernel message — not SSH related',
      },
      {
        log: 'Jun 14 14:10:00 fw01 sshd[6015]: Server listening on 0.0.0.0 port 22.',
        malicious: false,
        reason: 'Normal sshd startup',
      },
      {
        log: 'Jun 14 14:11:00 fw01 sshd[6020]: Connection closed by 10.0.0.1 port 22',
        malicious: false,
        reason: 'Normal connection close',
      },
    ],
    decoderHint: `<!-- Hint: Your decoder needs to be specific enough to match only real attack patterns -->
<decoder name="ssh-real-attack">
  <program_name>sshd</program_name>
  <prematch>Failed password</prematch>
  <!-- Think about what fields distinguish real from injected -->
</decoder>`,
    ruleHint: `<!-- Hint: Real attacks come from external IPs (203.0.113.x), not internal -->
<rule id="100040" level="10">
  <!-- How do you match only external source IPs? -->
</rule>`,
    solutionDecoder: `<decoder name="ssh-real-attack">
  <program_name>sshd</program_name>
  <prematch>Failed password</prematch>
  <regex>Failed password for (\\S+) from (\\S+) port</regex>
  <order>dstuser,srcip</order>
</decoder>`,
    solutionRule: `<rule id="100040" level="10">
  <decoded_as>ssh-real-attack</decoded_as>
  <regex>from 203\\.0\\.113\\.\\d+ port</regex>
  <description>SSH brute force from external IP</description>
  <mitre>
    <id>T1110</id>
    <tactic>Credential Access</tactic>
  </mitre>
</rule>`,
  },
];

/** Get a scenario by ID */
export function getScenario(id: string): Scenario | undefined {
  return scenarios.find((s) => s.id === id);
}

/** Preset log sets for sandbox mode */
export const presetLogs: { label: string; logs: string }[] = [
  {
    label: 'SSH Auth Logs',
    logs: [
      'Jun 14 15:16:01 web01 sshd[2001]: Failed password for invalid user admin from 10.0.0.50 port 22 ssh2',
      'Jun 14 15:16:02 web01 sshd[2002]: Failed password for invalid user root from 10.0.0.50 port 22 ssh2',
      'Jun 14 15:17:01 web01 sshd[2010]: Accepted publickey for deploy from 192.168.1.10 port 22 ssh2',
      'Jun 14 15:18:30 web01 sshd[2015]: Accepted password for admin from 192.168.1.20 port 22 ssh2',
    ].join('\n'),
  },
  {
    label: 'Apache Access Logs',
    logs: [
      'Jun 14 10:00:01 web01 apache[5001]: 10.0.0.99 - - "GET /uploads/shell.php?cmd=whoami HTTP/1.1" 200 45',
      'Jun 14 10:01:00 web01 apache[5010]: 192.168.1.5 - - "GET /index.html HTTP/1.1" 200 5120',
      'Jun 14 10:01:01 web01 apache[5011]: 192.168.1.5 - - "GET /css/style.css HTTP/1.1" 200 2048',
      'Jun 14 10:02:00 web01 apache[5020]: 192.168.1.10 - - "POST /api/login HTTP/1.1" 200 256',
    ].join('\n'),
  },
  {
    label: 'Sudo Logs',
    logs: [
      'Jun 14 12:00:01 db01 sudo[7001]: webuser : TTY=pts/0 ; PWD=/tmp ; USER=root ; COMMAND=/bin/bash',
      'Jun 14 12:05:00 db01 sudo[7010]: admin : TTY=pts/1 ; PWD=/home/admin ; USER=root ; COMMAND=/usr/bin/systemctl restart nginx',
      'Jun 14 12:06:00 db01 sudo[7011]: admin : TTY=pts/1 ; PWD=/home/admin ; USER=root ; COMMAND=/usr/bin/apt update',
      'Jun 14 12:08:00 db01 CRON[8001]: pam_unix(cron:session): session opened for user root',
    ].join('\n'),
  },
];
